import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Pagination, Stack, Button,
} from "@mui/material";
import { format } from "date-fns";
import API from "../../utils/Axios.utils";

const ACTION_COLORS = {
  CREATE: { bg: "#D1FAE5", color: "#065F46" },
  UPDATE: { bg: "#DBEAFE", color: "#1E40AF" },
  DELETE: { bg: "#FEE2E2", color: "#991B1B" },
  LOGIN: { bg: "#F3F4F6", color: "#374151" },
  LOGOUT: { bg: "#F3F4F6", color: "#374151" },
  PASSWORD_CHANGE: { bg: "#FEF9C3", color: "#713F12" },
  VIEW: { bg: "#F0FDF4", color: "#166534" },
};

const ACTION_OPTIONS = [
  "ALL", "CREATE", "UPDATE", "DELETE",
  "LOGIN", "LOGOUT", "PASSWORD_CHANGE"
];

const ActionChip = ({ action }) => {
  const meta = ACTION_COLORS[action] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <Chip
      label={action}
      size="small"
      sx={{
        backgroundColor: meta.bg,
        color: meta.color,
        fontWeight: 600,
        fontSize: "0.7rem",
        letterSpacing: "0.03em",
      }}
    />
  );
};

const formatDescription = (description, email, entity, jobCode, entityId, candidateName, reqCode) => {
  if (!description) return "—";
  let desc = description.trim();
  if (email && desc.toLowerCase().startsWith(email.toLowerCase())) {
    desc = desc.slice(email.length).trim();
  } else if (/^user#\d+\s+/i.test(desc)) {
    desc = desc.replace(/^user#\d+\s+/i, '');
  } else if (/^\S+@\S+\s+/.test(desc)) {
    desc = desc.replace(/^\S+@\S+\s+/, '');
  } else if (/^system\s+/i.test(desc)) {
    desc = desc.replace(/^system\s+/i, '');
  }

  if (entity === 'Job' && jobCode) {
    if (entityId) {
      desc = desc.replace(new RegExp(`job #${entityId}\\b`, 'gi'), () => `Job (${jobCode})`);
      desc = desc.replace(new RegExp(`#${entityId}\\b`, 'g'), () => `(${jobCode})`);
    }
    if (!desc.includes(`(${jobCode})`)) {
      if (/^created job$/i.test(desc.trim())) {
        desc = `created Job (${jobCode})`;
      } else if (/^updated job status$/i.test(desc.trim()) || /^updated status of job$/i.test(desc.trim())) {
        desc = `updated status of Job (${jobCode})`;
      } else if (/^updated job status\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated job status\s+(.+)$/i);
        desc = `updated status of Job (${jobCode}) ${match[1]}`;
      } else if (/^updated status of job\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated status of job\s+(.+)$/i);
        desc = `updated status of Job (${jobCode}) ${match[1]}`;
      } else if (/^updated job$/i.test(desc.trim())) {
        desc = `updated Job (${jobCode})`;
      } else {
        desc = desc.replace(/\bjob\b(?!\s*\()/gi, () => `Job (${jobCode})`);
      }
    }
    const escapedJob = jobCode.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    desc = desc.replace(new RegExp(`\\(${escapedJob}\\)(\\s*\\(${escapedJob}\\))+`, 'gi'), `(${jobCode})`);
  } else if (entity === 'Candidate' && candidateName) {
    if (entityId) {
      desc = desc.replace(new RegExp(`candidate #${entityId}\\b`, 'gi'), () => `Candidate (${candidateName})`);
      desc = desc.replace(new RegExp(`#${entityId}\\b`, 'g'), () => `(${candidateName})`);
    }
    if (!desc.includes(`(${candidateName})`)) {
      if (/^created candidate$/i.test(desc.trim())) {
        desc = `created Candidate (${candidateName})`;
      } else if (/^updated candidate status$/i.test(desc.trim()) || /^updated status of candidate$/i.test(desc.trim())) {
        desc = `updated status of Candidate (${candidateName})`;
      } else if (/^updated candidate status\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated candidate status\s+(.+)$/i);
        desc = `updated status of Candidate (${candidateName}) ${match[1]}`;
      } else if (/^updated status of candidate\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated status of candidate\s+(.+)$/i);
        desc = `updated status of Candidate (${candidateName}) ${match[1]}`;
      } else if (/^updated candidate$/i.test(desc.trim())) {
        desc = `updated Candidate (${candidateName})`;
      } else {
        desc = desc.replace(/\bcandidate\b(?!\s*\()/gi, () => `Candidate (${candidateName})`);
      }
    }
    const escapedCand = candidateName.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    desc = desc.replace(new RegExp(`\\(${escapedCand}\\)(\\s*\\(${escapedCand}\\))+`, 'gi'), `(${candidateName})`);
  } else if ((entity === 'Requisition' || entity === 'Requisitions') && reqCode) {
    if (entityId) {
      desc = desc.replace(new RegExp(`requisitions? #${entityId}\\b`, 'gi'), () => `Requisition (${reqCode})`);
      desc = desc.replace(new RegExp(`#${entityId}\\b`, 'g'), () => `(${reqCode})`);
    }
    if (!desc.includes(`(${reqCode})`)) {
      if (/^created requisitions?$/i.test(desc.trim())) {
        desc = `created Requisition (${reqCode})`;
      } else if (/^updated requisitions? status$/i.test(desc.trim()) || /^updated status of requisitions?$/i.test(desc.trim())) {
        desc = `updated status of Requisition (${reqCode})`;
      } else if (/^updated requisitions? status\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated requisitions? status\s+(.+)$/i);
        desc = `updated status of Requisition (${reqCode}) ${match[1]}`;
      } else if (/^updated status of requisitions?\s+(.+)$/i.test(desc.trim())) {
        const match = desc.trim().match(/^updated status of requisitions?\s+(.+)$/i);
        desc = `updated status of Requisition (${reqCode}) ${match[1]}`;
      } else if (/^updated requisitions?$/i.test(desc.trim())) {
        desc = `updated Requisition (${reqCode})`;
      } else {
        desc = desc.replace(/\brequisitions?\b(?!\s*\()/gi, () => `Requisition (${reqCode})`);
      }
    }
    const escapedReq = reqCode.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    desc = desc.replace(new RegExp(`\\(${escapedReq}\\)(\\s*\\(${escapedReq}\\))+`, 'gi'), `(${reqCode})`);
  }
  return desc ? desc.charAt(0).toUpperCase() + desc.slice(1) : description;
};

const AuditLogsContent = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const pageSize = 20;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `/api/audit?page=${page}&pageSize=${pageSize}`;
      if (actionFilter !== "ALL") url += `&action=${actionFilter}`;
      if (fromDate) url += `&from=${fromDate}`;
      if (toDate) url += `&to=${toDate}`;

      const res = await API.get(url);
      const data = res?.data;
      setLogs(data?.logs || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, actionFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClearFilters = () => {
    setActionFilter("ALL");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  return (
    <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" fontWeight={700}>Audit Logs</Typography>
        <Typography variant="body2" color="text.secondary">
          Track all user actions across the platform.
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Action</InputLabel>
          <Select
            value={actionFilter}
            label="Action"
            onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          >
            {ACTION_OPTIONS.map((a) => (
              <MenuItem key={a} value={a}>{a === "ALL" ? "All Actions" : a}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="From"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => { setToDate(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />

        {(actionFilter !== "ALL" || fromDate || toDate) && (
          <Button size="small" onClick={handleClearFilters} sx={{ color: "#6B7280", textTransform: "none" }}>
            Clear filters
          </Button>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {total} record{total !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { color: "#6B7280", fontSize: "0.75rem", fontWeight: 600, letterSpacing: "0.05em", borderBottom: "1px solid #E5E7EB" } }}>
              <TableCell>TIMESTAMP</TableCell>
              <TableCell>ACTION</TableCell>
              <TableCell>ENTITY</TableCell>
              <TableCell>PERFORMED BY</TableCell>
              <TableCell>DESCRIPTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, color: "#9CA3AF" }}>
                  No audit logs yet. Actions performed by users will appear here.
                </TableCell>
              </TableRow>
            ) : (
              logs
                .filter((log) => !(log.entity === 'Job' && !log.entity_id && !log.job_code))
                .map((log) => (
                <TableRow key={log.id} sx={{ "& td": { borderBottom: "1px solid #F3F4F6", py: 1.25 } }}>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                      {log.createdAt
                        ? format(new Date(log.createdAt), "dd MMM yyyy, HH:mm")
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ActionChip action={log.action} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {log.entity || "—"}
                      {log.entity === 'Job' && (log.job_code || (log.entity_id && !/^\d+$/.test(log.entity_id) ? log.entity_id : null)) ? (
                        <span style={{ color: "#2563EB", fontWeight: 600 }}> ({log.job_code || log.entity_id})</span>
                      ) : log.entity === 'Candidate' && (log.candidate_name || log.official_name) ? (
                        <span style={{ color: "#2563EB", fontWeight: 600 }}> ({log.candidate_name || log.official_name})</span>
                      ) : log.entity === 'Candidate' && log.entity_id ? (
                        <span style={{ color: "#2563EB", fontWeight: 600 }}> (#{log.entity_id})</span>
                      ) : (log.entity === 'Requisition' || log.entity === 'Requisitions') && (log.requisition_id || log.official_name) ? (
                        <span style={{ color: "#2563EB", fontWeight: 600 }}> ({log.requisition_id || log.official_name})</span>
                      ) : (log.official_name || (log.entity_id && !/^\d+$/.test(log.entity_id) ? log.entity_id : null)) ? (
                        <span style={{ color: "#2563EB", fontWeight: 600 }}> ({log.official_name || log.entity_id})</span>
                      ) : null}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.performed_by_email || (log.performed_by ? `User #${log.performed_by}` : "User")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      title={formatDescription(log.description, log.performed_by_email, log.entity, log.job_code, log.entity_id, log.candidate_name || log.official_name, log.requisition_id || log.official_name)}
                      sx={{ maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {formatDescription(log.description, log.performed_by_email, log.entity, log.job_code, log.entity_id, log.candidate_name || log.official_name, log.requisition_id || log.official_name)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack alignItems="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, val) => setPage(val)}
            size="small"
            color="primary"
          />
        </Stack>
      )}
    </Paper>
  );
};

export default AuditLogsContent;
